import { useCallback, useEffect, useState } from 'react';
import { useBatchId } from '../../../hooks/useBatchId';
import { useTranslation } from 'react-i18next';
import { PROPOSER_LOAD_VIEW } from '../../../shared/Routes';
import { Button, Card, Table, TableColumnsType, Typography } from 'antd';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import Icon from '../../../components/ui-components/Icon';
import { FunctionaryInfo } from '../../../shared/VolunteerApplicationServiceTypes';
import { useProposerLoad } from './useProposerLoad';
import { FilterProposerRegion } from './FilterProposerRegion';
import { tablePaginationConfiguration } from '../../../shared/Utils';

const { Text } = Typography;

export const ProposerReAssign = () => {
	const {
		applicationRecord,
		functionaryList,
		onPageChange,
		filter,
		onFilterChange,
		paging,
		onSubmitClick,
	} = useProposerLoad();
	const { pagination } = functionaryList;
	const { t } = useTranslation();
	const { navigatewithZoneId } = useBatchId();
	const [tableColumnValues, setTableColumnValues] = useState<FunctionaryInfo[]>(
		[]
	);
	const [selectedRow, setSelectedRow] = useState<FunctionaryInfo | null>(null);

	useEffect(() => {
		setTableColumnValues(functionaryList.functionary ?? []);
	}, [functionaryList.functionary]);

	const tablePaginationConfig = tablePaginationConfiguration(
		paging,
		pagination,
		onPageChange
	);

	const onSelectChange = (_keys: React.Key[], rows: FunctionaryInfo[]) => {
		setSelectedRow(rows[0]);
	};

	const proposerReAssignSelection = {
		type: 'radio' as const,
		selectedRowKeys: selectedRow ? [selectedRow.srcmId] : [],
		onChange: onSelectChange,
	};

	const isSelected = selectedRow;

	const baseColumns: TableColumnsType<FunctionaryInfo> = [
		{
			title: t('proposerName'),
			dataIndex: 'printName',
			key: 'proposerName',
		},
		{
			title: t('proposerSrcmId'),
			dataIndex: 'srcmId',
			key: 'proposerSrcmId',
		},
		{
			title: t('proposerGender'),
			dataIndex: 'gender',
			key: 'proposerGender',
		},
		{
			title: t('proposerLoad'),
			dataIndex: 'applicationCount',
			key: 'proposerLoad',
			sorter: (a, b) => (a.applicationCount ?? 0) - (b.applicationCount ?? 0),
			defaultSortOrder: 'ascend',
			render: (value: string | number) => value ?? 0,
		},
	];

	const handleBackClick = useCallback(() => {
		navigatewithZoneId(PROPOSER_LOAD_VIEW);
	}, [navigatewithZoneId]);

	const handleReAssign = useCallback(() => {
		if (selectedRow?.id) {
			onSubmitClick(Number(selectedRow.id));
		}
	}, [selectedRow, onSubmitClick]);

	return (
		<>
			<div className="md:hidden flex justify-center items-center pt-3 pb-1 relative">
				<Text className="font-sans font-bold text-lg mb-1">{t('view')}</Text>
			</div>
			<div className="flex justify-between items-center pt-1 pb-3 px-5 md:px-5 md:py-5 relative">
				{/* Back Button - Desktop */}
				<Button
					type="primary"
					onClick={handleBackClick}
					className="hidden md:block font-bold text-sm"
				>
					<span className="flex gap-2">
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</span>
				</Button>

				{/* Back Button + Title - Mobile */}
				<div className="block sm:hidden">
					<a
						onClick={handleBackClick}
						className="relative top:0 md:top-[3px] mr-2 font-bold text-sm"
					>
						<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
					</a>
				</div>

				{/* Centered Title */}
				<Text className="hidden md:block font-sans font-bold text-xl mb-1 absolute left-1/2 transform -translate-x-1/2">
					{`${t('ProposerLoadDetailsofZone')} - ${
						applicationRecord?.candidateZoneName ?? ''
					}`}
				</Text>

				<FilterProposerRegion value={filter} onFilterChange={onFilterChange} />
			</div>
			<div className="hidden md:block px-4">
				<div className="pb-4">
					<Card
						title={
							<div className="flex row items-center">
								<div className="pl-[8px]">{t('candidateDetails')}</div>
							</div>
						}
					>
						<div className="pl-[8px]">
							<div className="grid grid-cols-4 gap-x-6">
								{/* Header row */}
								<p className="font-medium my-0">{t('candidateName')}</p>
								<p className="font-medium my-0">{t('candidateSrcmId')}</p>
								<p className="font-medium my-0">{t('candidateGender')}</p>
								<p className="font-medium my-0">{t('candidateZone')}</p>
								<>
									<p>{applicationRecord?.candidateName}</p>
									<p>{applicationRecord?.candidateSrcmId}</p>
									<p>{applicationRecord?.candidateGender}</p>
									<p>{applicationRecord?.candidateZoneName}</p>
								</>
							</div>
						</div>
					</Card>
				</div>
				<div className="border rounded-xl shadow-sm bg-white">
					<Table
						rowKey="srcmId"
						columns={baseColumns}
						rowSelection={proposerReAssignSelection}
						dataSource={tableColumnValues}
						pagination={tablePaginationConfig}
						scroll={{ x: 'max-content' }}
						rowClassName={() => 'border-b border-gray-300 bg-white'}
						className="w-full overflow-x-auto !border-separate !border-spacing-y-0"
					/>
					{isSelected && (
						<div className="flex justify-end mt-4">
							<Button type="primary" onClick={handleReAssign}>
								{t('assign')}
							</Button>
						</div>
					)}
				</div>
			</div>
		</>
	);
};
